import { useState } from "react";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

export type TranscriptSegment = {
	id: string;
	content: string;
	speakerRole: string;
	speakerId?: string;
	segmentId?: string;
	segmentType?: string;
	segmentTitle?: string;
};

export type TranscriptViewerProps = {
	segments: TranscriptSegment[];
	defaultExpanded?: boolean;
	expanded?: boolean;
	onToggle?: () => void;
	showLabel?: string;
	hideLabel?: string;
};

const TranscriptViewer = ({
	segments,
	defaultExpanded = false,
	expanded,
	onToggle,
	showLabel,
	hideLabel,
}: TranscriptViewerProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
	const isExpanded = expanded ?? internalExpanded;
	const resolvedShowLabel = showLabel ?? t("transcript.show");
	const resolvedHideLabel = hideLabel ?? t("transcript.hide");

	const handleToggle = () => {
		if (expanded == null) {
			setInternalExpanded((prev) => !prev);
		}
		onToggle?.();
	};

	if (segments.length === 0) {
		return null;
	}

	return (
		<Box
			style={{
				borderRadius: theme.roundness * 2,
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant,
				backgroundColor: theme.colors.surface,
				gap: 8,
			}}
		>
			<List.Item
				title={isExpanded ? resolvedHideLabel : resolvedShowLabel}
				titleStyle={{
					color: theme.colors.onSurfaceVariant,
				}}
				left={(props) => (
					<List.Icon
						{...props}
						icon={isExpanded ? "chevron-up" : "chevron-down"}
						color={theme.colors.onSurface}
					/>
				)}
				onPress={handleToggle}
			/>
			{isExpanded && (
				<Box gap={4} paddingHorizontal={12} paddingBottom={12}>
					{segments.map((segment) => (
						<Text
							key={segment.id}
							variant="bodySmall"
							style={{ lineHeight: 18 }}
						>
							<Text
								variant="bodySmall"
								style={{ fontWeight: "700", lineHeight: 18 }}
							>
								{segment.speakerRole}:
							</Text>{" "}
							{segment.content}
						</Text>
					))}
				</Box>
			)}
		</Box>
	);
};

export default TranscriptViewer;
